import { type KeyboardEvent, type RefObject, useCallback, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const useFocusTrap = ({ enabled }: { enabled: boolean }) => {
	const containerRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (enabled) {
			const firstFocusable = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
			firstFocusable?.focus();
		}
	}, [enabled]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			if (e.key !== "Tab" || !enabled) {
				return;
			}

			const container = containerRef.current;
			if (!container) {
				return;
			}

			const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
			if (focusableElements.length === 0) {
				return;
			}

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];
			const activeElement = document.activeElement;
			const focusIsOutside = !activeElement || !container.contains(activeElement) || activeElement === container;

			if (focusIsOutside) {
				e.preventDefault();
				(e.shiftKey ? lastElement : firstElement)?.focus();
				return;
			}

			if (e.shiftKey && activeElement === firstElement) {
				e.preventDefault();
				lastElement?.focus();
			} else if (!e.shiftKey && activeElement === lastElement) {
				e.preventDefault();
				firstElement?.focus();
			}
		},
		[enabled],
	);

	return {
		containerRef: containerRef as RefObject<HTMLElement>,
		handleKeyDown,
	};
};
